import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { EyeInvisibleOutlined, EyeOutlined, GlobalOutlined } from "@ant-design/icons";
import { ApiError } from "@anyseal/api-client";
import { BrandMark, Button, Card, Field, Input, RequestId, Segmented } from "@anyseal/ui";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { portalClient } from "../api";
import { safeOAuthAuthorizationURL } from "../oauth";

interface OAuthProvider { slug: string; display_name: string; protocol: string; }
interface OAuthStart { authorization_url: string; expires_at: string; }

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_LENGTH = 6;

type FieldErrors = Record<string, string | undefined>;

/* Brand-panel copy restates the landing page's factual claims (PRODUCT.md
 * §9.3: 准确、克制、直接) — no marketing slogans. Kept deliberately quiet:
 * one headline plus a single muted feature line. */
const PANEL_COPY = {
  "zh-CN": {
    title: "统一接口，\n接入所有模型",
    tagline: "智能路由 · 实时追踪 · 用量透明",
  },
  en: {
    title: "The unified interface\nfor every model",
    tagline: "Smart routing · Live tracing · Usage transparency",
  },
} as const;

function useZh(locale: "zh-CN" | "en"): boolean {
  return locale === "zh-CN";
}

/** Password entry with a show/hide toggle. Kept local to the auth pages so
 *  the shared Input stays a plain field for the rest of the portal. */
function PasswordInput({ value, onChange, autoComplete, invalid, autoFocus }: {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  autoComplete: string;
  invalid?: boolean;
  autoFocus?: boolean;
}) {
  const { locale } = useI18n();
  const zh = useZh(locale);
  const [visible, setVisible] = useState(false);
  const toggleLabel = visible ? (zh ? "隐藏密码" : "Hide password") : zh ? "显示密码" : "Show password";
  return <span className="portal-input-affix">
    <Input type={visible ? "text" : "password"} value={value} onChange={onChange} autoComplete={autoComplete} aria-invalid={invalid || undefined} autoFocus={autoFocus} />
    <button
      type="button"
      className="portal-input-affix__toggle"
      aria-pressed={visible}
      aria-label={toggleLabel}
      title={toggleLabel}
      onClick={() => setVisible((current) => !current)}
    >
      {visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
    </button>
  </span>;
}

/* 0 = untouched (meter hidden), 1–4 maps to the tone ladder. */
function strengthScore(value: string): number {
  if (!value) return 0;
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((pattern) => pattern.test(value)).length;
  let score = 0;
  if (value.length >= 8) score += 1;
  if (value.length >= 12) score += 1;
  if (classes >= 2) score += 1;
  if (classes >= 3) score += 1;
  if (value.length < 8) score = Math.min(score, 1);
  return Math.max(1, Math.min(4, score));
}

function StrengthMeter({ value, locale }: { value: string; locale: "zh-CN" | "en" }) {
  const score = strengthScore(value);
  if (!score) return null;
  const labels = locale === "zh-CN" ? ["弱", "一般", "良好", "强"] : ["Weak", "Fair", "Good", "Strong"];
  return <div className={`portal-strength portal-strength--${score}`}>
    <div className="portal-strength__bars" role="img" aria-label={locale === "zh-CN" ? `密码强度：${labels[score - 1]}` : `Password strength: ${labels[score - 1]}`}>
      <span /><span /><span /><span />
    </div>
    <span className="portal-strength__label" aria-hidden="true">{labels[score - 1]}</span>
  </div>;
}

/** Six-box one-time-code entry: auto-advance, backspace to the previous box,
 *  paste distribution, and auto-submit once all digits are present. Recovery
 *  codes stay on a free-text field (see LoginPage's codeMode). */
function CodeInput({ onComplete, busy, invalid, label }: {
  onComplete: (code: string) => void;
  busy: boolean;
  invalid?: boolean;
  label: string;
}) {
  const [digits, setDigits] = useState<string[]>(() => Array<string>(CODE_LENGTH).fill(""));
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  useEffect(() => { inputs.current[0]?.focus(); }, []);

  const commit = (next: string[]) => {
    setDigits(next);
    if (next.every((digit) => digit !== "")) onComplete(next.join(""));
  };

  const setDigit = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, "");
    const next = [...digits];
    if (cleaned.length > 1) {
      for (let offset = 0; offset < cleaned.length && index + offset < CODE_LENGTH; offset += 1) next[index + offset] = cleaned.charAt(offset);
      commit(next);
      inputs.current[Math.min(index + cleaned.length, CODE_LENGTH - 1)]?.focus();
      return;
    }
    next[index] = cleaned;
    commit(next);
    if (cleaned && index < CODE_LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      const next = [...digits];
      next[index - 1] = "";
      setDigits(next);
      inputs.current[index - 1]?.focus();
    } else if (event.key === "ArrowLeft" && index > 0) inputs.current[index - 1]?.focus();
    else if (event.key === "ArrowRight" && index < CODE_LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  return <div className="portal-code-input">
    {digits.map((digit, index) => <input
      key={index}
      ref={(element) => { inputs.current[index] = element; }}
      value={digit}
      disabled={busy}
      inputMode="numeric"
      autoComplete={index === 0 ? "one-time-code" : "off"}
      aria-label={`${label} ${index + 1}/${CODE_LENGTH}`}
      aria-invalid={invalid || undefined}
      onChange={(event) => setDigit(index, event.target.value)}
      onKeyDown={(event) => handleKeyDown(index, event)}
      onFocus={(event) => event.target.select()}
    />)}
  </div>;
}

export function LoginPage() {
  const { status, login, completeTwoFactor } = useAuth();
  const { locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const zh = useZh(locale);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [flowToken, setFlowToken] = useState("");
  const [code, setCode] = useState("");
  const [codeMode, setCodeMode] = useState<"totp" | "recovery">("totp");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const providers = useQuery({ queryKey: ["portal", "oauth-providers"], queryFn: () => portalClient.get<OAuthProvider[]>("/api/portal/v1/auth/oauth/providers", { authenticated: false }) });
  const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";
  if (status === "authenticated") return <Navigate to={from} replace />;

  const finishTwoFactor = async (value: string) => {
    if (busy || !flowToken) return;
    setError(null);
    setBusy(true);
    try { await completeTwoFactor(flowToken, value); navigate(from, { replace: true }); }
    catch (requestError) { if (requestError instanceof ApiError) setError(requestError); }
    finally { setBusy(false); }
  };

  const clearFieldError = (key: string) => setFieldErrors((current) => (current[key] ? { ...current, [key]: undefined } : current));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError(null);
    if (flowToken) {
      const value = code.trim();
      if (!value) { setFieldErrors({ code: zh ? "请输入验证码" : "Enter the verification code" }); return; }
      if (codeMode === "totp" && !/^\d{6}$/.test(value)) { setFieldErrors({ code: zh ? "请输入 6 位数字验证码" : "Enter the six-digit code" }); return; }
      setFieldErrors({});
      await finishTwoFactor(value);
      return;
    }
    const next: FieldErrors = {};
    if (!email.trim()) next.email = zh ? "请输入邮箱" : "Enter your email";
    else if (!EMAIL_PATTERN.test(email.trim())) next.email = zh ? "邮箱格式不正确" : "Enter a valid email address";
    if (!password) next.password = zh ? "请输入密码" : "Enter your password";
    if (Object.values(next).some(Boolean)) { setFieldErrors(next); return; }
    setFieldErrors({});
    setBusy(true);
    try {
      const result = await login(email.trim(), password);
      if (result.challenge) { setFlowToken(result.challenge.flow_token); setCode(""); setCodeMode("totp"); return; }
      navigate(from, { replace: true });
    } catch (requestError) { if (requestError instanceof ApiError) setError(requestError); }
    finally { setBusy(false); }
  };

  const startOAuth = async (provider: OAuthProvider) => {
    setBusy(true); setError(null);
    try {
      const start = await portalClient.post<OAuthStart>(`/api/portal/v1/auth/oauth/${encodeURIComponent(provider.slug)}/start`, undefined, { authenticated: false });
      window.location.assign(safeOAuthAuthorizationURL(start.authorization_url));
    } catch (requestError) { if (requestError instanceof ApiError) setError(requestError); setBusy(false); }
  };

  return <AuthLayout locale={locale} setLocale={setLocale}>
    <Card className="portal-auth-card">
      <div className="portal-auth-card__head">
        {flowToken
          ? <div className="portal-auth-steps"><span className="portal-auth-steps__dot is-done" aria-hidden="true" /><span className="portal-auth-steps__dot is-active" aria-hidden="true" /><span className="portal-auth-steps__text">{zh ? "第 2 步，共 2 步 · 双因素验证" : "Step 2 of 2 · Two-factor"}</span></div>
          : <span>{zh ? "安全访问开发者工作区" : "Secure access to your developer workspace"}</span>}
        <h1>{flowToken ? (zh ? "双因素验证" : "Two-factor verification") : zh ? "登录 LonMax AI" : "Sign in to LonMax AI"}</h1>
        <p>{flowToken ? (zh ? "输入验证器中的 6 位代码，或切换为恢复码。" : "Enter the six-digit code from your authenticator, or switch to a recovery code.") : zh ? "管理 API 密钥、模型、用量和账单。" : "Manage API keys, models, usage, and billing."}</p>
      </div>
      <form noValidate onSubmit={(event) => void submit(event)} className="portal-auth-form">
        {flowToken ? (codeMode === "totp"
          ? <Field label={zh ? "验证码" : "Verification code"} error={fieldErrors.code} required>
            <CodeInput label={zh ? "验证码" : "Verification code"} busy={busy} invalid={Boolean(fieldErrors.code)} onComplete={(value) => { setCode(value); void finishTwoFactor(value); }} />
          </Field>
          : <Field label={zh ? "恢复码" : "Recovery code"} error={fieldErrors.code} required>
            <Input value={code} onChange={(event) => { setCode(event.target.value); clearFieldError("code"); }} autoComplete="off" spellCheck={false} autoFocus aria-invalid={fieldErrors.code ? true : undefined} />
          </Field>)
          : <>
            <Field label={zh ? "邮箱" : "Email"} error={fieldErrors.email} required>
              <Input type="email" autoComplete="email" autoFocus value={email} onChange={(event) => { setEmail(event.target.value); clearFieldError("email"); }} aria-invalid={fieldErrors.email ? true : undefined} />
            </Field>
            <Field label={zh ? "密码" : "Password"} error={fieldErrors.password} required>
              <PasswordInput value={password} onChange={(event) => { setPassword(event.target.value); clearFieldError("password"); }} autoComplete="current-password" invalid={Boolean(fieldErrors.password)} />
            </Field>
          </>}
        {error ? <div className="portal-form-error" role="alert"><strong>{flowToken ? (zh ? "验证未通过" : "Verification failed") : zh ? "登录未完成" : "Sign-in not completed"}</strong><span>{error.message}</span>{error.requestId ? <RequestId value={error.requestId} /> : null}</div> : null}
        <Button type="submit" busy={busy}>{flowToken ? (zh ? "验证并登录" : "Verify and sign in") : zh ? "登录" : "Sign in"}</Button>
        {flowToken ? <div className="portal-auth-quiet">
          <Button type="button" variant="quiet" onClick={() => { setCodeMode(codeMode === "totp" ? "recovery" : "totp"); setCode(""); setFieldErrors({}); }}>{codeMode === "totp" ? (zh ? "使用恢复码" : "Use a recovery code") : zh ? "使用验证器代码" : "Use authenticator code"}</Button>
          <Button type="button" variant="quiet" onClick={() => { setFlowToken(""); setCode(""); setCodeMode("totp"); setError(null); setFieldErrors({}); }}>{zh ? "返回账号登录" : "Back to password sign-in"}</Button>
        </div> : <>
          {(providers.data?.length ?? 0) > 0 ? <>
            <div className="portal-auth-divider"><span>{zh ? "或使用身份提供商" : "or use an identity provider"}</span></div>
            <div className="portal-auth-providers">
              {providers.data?.map((provider) => <Button key={provider.slug} type="button" variant="secondary" disabled={busy} onClick={() => void startOAuth(provider)}>{zh ? `使用 ${provider.display_name} 登录` : `Continue with ${provider.display_name}`}</Button>)}
            </div>
          </> : null}
          <div className="portal-auth-links"><Link to="/password-reset">{zh ? "忘记密码？" : "Forgot password?"}</Link><Link to="/register">{zh ? "创建账号" : "Create account"}</Link></div>
        </>}
      </form>
    </Card>
  </AuthLayout>;
}

export function RegisterPage() {
  const { status, register } = useAuth();
  const { locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const zh = useZh(locale);
  const [form, setForm] = useState({ email: "", password: "", display_name: "", organization_display_name: "", default_currency: "CNY" as "CNY" | "USD", referral_code: "", invitation_code: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  if (status === "authenticated") return <Navigate to="/" replace />;

  const update = (key: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => (current[key] ? { ...current, [key]: undefined } : current));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError(null);
    const next: FieldErrors = {};
    if (!form.display_name.trim()) next.display_name = zh ? "请输入姓名" : "Enter your name";
    if (!form.organization_display_name.trim()) next.organization_display_name = zh ? "请输入组织名称" : "Enter an organization name";
    if (!form.email.trim()) next.email = zh ? "请输入邮箱" : "Enter your email";
    else if (!EMAIL_PATTERN.test(form.email.trim())) next.email = zh ? "邮箱格式不正确" : "Enter a valid email address";
    if (form.password.length < 12) next.password = zh ? "密码至少需要 12 位" : "Use at least 12 characters";
    if (Object.values(next).some(Boolean)) { setFieldErrors(next); return; }
    setFieldErrors({});
    setBusy(true);
    try {
      await register({ ...form, display_name: form.display_name.trim(), organization_display_name: form.organization_display_name.trim(), email: form.email.trim(), locale });
      navigate("/dashboard", { replace: true });
    } catch (requestError) { if (requestError instanceof ApiError) setError(requestError); }
    finally { setBusy(false); }
  };

  return <AuthLayout locale={locale} setLocale={setLocale}>
    <Card className="portal-auth-card portal-auth-card--wide">
      <div className="portal-auth-card__head"><span>{zh ? "创建开发者工作区" : "Create a developer workspace"}</span><h1>{zh ? "开始使用 LonMax AI" : "Get started with LonMax AI"}</h1><p>{zh ? "注册后会自动创建一个组织和默认项目。" : "Registration creates your organization and default project."}</p></div>
      <form noValidate onSubmit={(event) => void submit(event)} className="portal-auth-form">
        <section className="portal-auth-group">
          <h2 className="portal-auth-group__title">{zh ? "账号信息" : "Account"}</h2>
          <Field label={zh ? "邮箱" : "Email"} error={fieldErrors.email} required>
            <Input type="email" autoComplete="email" value={form.email} onChange={(event) => update("email", event.target.value)} aria-invalid={fieldErrors.email ? true : undefined} />
          </Field>
          <Field label={zh ? "密码" : "Password"} error={fieldErrors.password} hint={zh ? "至少 12 位，建议使用密码管理器。" : "Use at least 12 characters and a password manager."} required>
            <PasswordInput value={form.password} onChange={(event) => update("password", event.target.value)} autoComplete="new-password" invalid={Boolean(fieldErrors.password)} />
            <StrengthMeter value={form.password} locale={locale} />
          </Field>
        </section>
        <section className="portal-auth-group">
          <h2 className="portal-auth-group__title">{zh ? "身份与组织" : "Identity and organization"}</h2>
          <div className="portal-auth-group__grid">
            <Field label={zh ? "姓名" : "Name"} error={fieldErrors.display_name} required>
              <Input autoComplete="name" value={form.display_name} onChange={(event) => update("display_name", event.target.value)} aria-invalid={fieldErrors.display_name ? true : undefined} />
            </Field>
            <Field label={zh ? "组织名称" : "Organization"} error={fieldErrors.organization_display_name} required>
              <Input autoComplete="organization" value={form.organization_display_name} onChange={(event) => update("organization_display_name", event.target.value)} aria-invalid={fieldErrors.organization_display_name ? true : undefined} />
            </Field>
          </div>
        </section>
        <section className="portal-auth-group">
          <h2 className="portal-auth-group__title">{zh ? "结算与邀请" : "Billing and invitation"}</h2>
          <div className="portal-auth-group__grid">
            <Field label={zh ? "结算币种" : "Billing currency"}>
              <Segmented label={zh ? "结算币种" : "Billing currency"} value={form.default_currency} onChange={(value) => update("default_currency", value)} options={[{ value: "CNY", label: "CNY" }, { value: "USD", label: "USD" }]} />
            </Field>
            <Field label={zh ? "推荐码（可选）" : "Referral code (optional)"}>
              <Input value={form.referral_code} onChange={(event) => update("referral_code", event.target.value)} />
            </Field>
          </div>
          <Field
            label={<>{zh ? "内测邀请码" : "Beta invitation code"} <span className="portal-auth-chip">{zh ? "内测邀请制" : "Invite-only beta"}</span></>}
            hint={zh ? "邀请制期间必填；公开注册开启后可留空。" : "Required during invite-only beta; optional after public registration opens."}
          >
            <Input autoComplete="off" value={form.invitation_code} onChange={(event) => update("invitation_code", event.target.value)} />
          </Field>
        </section>
        {error ? <div className="portal-form-error" role="alert"><strong>{zh ? "注册未完成" : "Registration not completed"}</strong><span>{error.message}</span>{error.requestId ? <RequestId value={error.requestId} /> : null}</div> : null}
        <div className="portal-auth-submit">
          <Button type="submit" busy={busy}>{zh ? "创建账号" : "Create account"}</Button>
          <span>{zh ? "已有账号？" : "Already registered?"} <Link to="/login">{zh ? "登录" : "Sign in"}</Link></span>
        </div>
      </form>
    </Card>
  </AuthLayout>;
}

export function PasswordResetPage() {
  const { locale, setLocale } = useI18n();
  const zh = useZh(locale);
  const routerLocation = useLocation();
  const params = new URLSearchParams(routerLocation.search);
  const [flowToken, setFlowToken] = useState(params.get("flow_token") ?? "");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      if (flowToken) await portalClient.post("/api/portal/v1/auth/password-reset/complete", { flow_token: flowToken, new_password: newPassword }, { authenticated: false });
      else await portalClient.post("/api/portal/v1/auth/password-reset/request", { email }, { authenticated: false });
      setComplete(true);
    } catch (requestError) { if (requestError instanceof ApiError) setError(requestError); }
    finally { setBusy(false); }
  };
  return <AuthLayout locale={locale} setLocale={setLocale}>
    <Card className="portal-auth-card">
      <div className="portal-auth-card__head"><span>{zh ? "账户恢复" : "Account recovery"}</span><h1>{flowToken ? (zh ? "设置新密码" : "Set a new password") : zh ? "重置密码" : "Reset your password"}</h1><p>{zh ? "为保护账户，系统不会透露邮箱是否已注册。" : "To protect accounts, the system never reveals whether an email is registered."}</p></div>
      {complete ? <div className="portal-notice"><strong>{flowToken ? (zh ? "密码已重置" : "Password reset") : zh ? "请求已受理" : "Request accepted"}</strong><p>{flowToken ? (zh ? "所有会话已撤销，请使用新密码登录。" : "All sessions were revoked. Sign in with the new password.") : zh ? "如果账号可以恢复，我们会发送操作说明。" : "If the account can be recovered, instructions will be sent."}</p><Link to="/login">{zh ? "返回登录" : "Back to sign in"}</Link></div>
        : <form className="portal-auth-form" noValidate onSubmit={(event) => void submit(event)}>
          {flowToken ? <>
            <Field label={zh ? "恢复令牌" : "Recovery token"} required>
              <Input value={flowToken} onChange={(event) => setFlowToken(event.target.value)} required />
            </Field>
            <Field label={zh ? "新密码" : "New password"} hint={zh ? "至少 12 位。" : "Use at least 12 characters."} required>
              <PasswordInput value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" />
              <StrengthMeter value={newPassword} locale={locale} />
            </Field>
          </> : <Field label={zh ? "登录邮箱" : "Sign-in email"} required>
            <Input type="email" autoComplete="email" autoFocus value={email} onChange={(event) => setEmail(event.target.value)} required />
          </Field>}
          {error ? <div className="portal-form-error" role="alert"><strong>{zh ? "操作未完成" : "Action not completed"}</strong><span>{error.message}</span>{error.requestId ? <RequestId value={error.requestId} /> : null}</div> : null}
          <Button type="submit" busy={busy}>{flowToken ? (zh ? "重置并撤销所有会话" : "Reset and revoke all sessions") : zh ? "发送恢复说明" : "Send recovery instructions"}</Button>
          <div className="portal-auth-quiet"><Link className="portal-auth-quiet__link" to="/login">{zh ? "返回登录" : "Back to sign in"}</Link></div>
        </form>}
    </Card>
  </AuthLayout>;
}

export function EmailVerificationPage() {
  const { locale, setLocale } = useI18n();
  const zh = useZh(locale);
  const routerLocation = useLocation();
  const token = new URLSearchParams(routerLocation.search).get("flow_token") ?? "";
  const [state, setState] = useState<"ready" | "busy" | "done" | "error">("ready");
  const [requestId, setRequestId] = useState("");
  const verify = async () => {
    setState("busy");
    try { await portalClient.post("/api/portal/v1/auth/email-verification/complete", { flow_token: token }, { authenticated: false }); setState("done"); }
    catch (requestError) { setRequestId(requestError instanceof ApiError ? requestError.requestId : ""); setState("error"); }
  };
  return <AuthLayout locale={locale} setLocale={setLocale}>
    <Card className="portal-auth-card">
      <div className="portal-auth-card__head"><span>{zh ? "邮箱安全" : "Email security"}</span><h1>{zh ? "验证邮箱" : "Verify email"}</h1><p>{state === "done" ? (zh ? "邮箱验证已完成。" : "Email verification is complete.") : state === "error" ? (zh ? "验证链接无效或已经过期。" : "The verification link is invalid or expired.") : (zh ? "确认此邮箱属于你的 LonMax AI 账户。" : "Confirm that this email belongs to your LonMax AI account.")}</p></div>
      {requestId ? <RequestId value={requestId} /> : null}
      {state === "ready" || state === "busy" ? <Button busy={state === "busy"} disabled={!token} onClick={() => void verify()}>{zh ? "确认邮箱" : "Verify email"}</Button> : null}
      <div className="portal-auth-quiet"><Link className="portal-auth-quiet__link" to="/login">{zh ? "返回登录" : "Back to sign in"}</Link></div>
    </Card>
  </AuthLayout>;
}

export function AuthLayout({ locale, setLocale, children }: { locale: "zh-CN" | "en"; setLocale: (value: "zh-CN" | "en") => void; children: ReactNode }) {
  const zh = useZh(locale);
  const copy = zh ? PANEL_COPY["zh-CN"] : PANEL_COPY.en;
  const switchLabel = zh ? "切换到英文" : "切换到中文";
  return <main className="portal-auth">
    <aside className="portal-auth-panel">
      <div className="portal-auth-panel__brand"><BrandMark size={26} /><strong>LonMax AI</strong></div>
      <div className="portal-auth-panel__body">
        <h2>{copy.title}</h2>
        <p className="portal-auth-panel__tagline">{copy.tagline}</p>
      </div>
      <div className="portal-auth-panel__foot">
        <span>© {new Date().getFullYear()} LonMax AI</span>
        <Link to="/legal/public">{zh ? "条款与隐私" : "Terms & privacy"}</Link>
      </div>
    </aside>
    <section className="portal-auth-main">
      <header className="portal-auth-main__bar">
        <div className="portal-auth__brand"><BrandMark size={26} /><strong>LonMax AI</strong></div>
        <button
          type="button"
          className="portal-auth__language"
          aria-label={switchLabel}
          title={switchLabel}
          onClick={() => setLocale(zh ? "en" : "zh-CN")}
        >
          <GlobalOutlined />
        </button>
      </header>
      <div className="portal-auth-main__content">{children}</div>
      <footer className="portal-auth-main__foot"><Link to="/legal/public">{zh ? "条款与隐私" : "Terms & privacy"}</Link></footer>
    </section>
  </main>;
}
